import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { jt, t } from "ttag";

import { getVerificationStatus } from "../../api/verification";
import { isoWebPageURL } from "../../models/nominals";
import { VerificationChannel } from "../../models/verification";
import { trackOtpEvent } from "../../utils/analytics";
import { usePageSetting } from "../../utils/settings";
import { usePageURL } from "../../utils/urls";
import { EmailVerificationGate } from "../EmailVerificationGate";
import { PhoneVerificationGate } from "../PhoneVerificationGate";

import styles from "./index.module.scss";

type IProps = Immutable<{
    email: string | null;
    phone: string | null;
    onVerified: () => void;
    onCancel: () => void;
}>;

type Phase = "checking" | "picker" | VerificationChannel;

const CHANNEL_ORDER: readonly VerificationChannel[] = ["email", "sms", "voice"];

const channelLabel = (channel: VerificationChannel): string => {
    switch (channel) {
        case "email":
            return t`Email`;
        case "sms":
            return t`Text message`;
        case "voice":
            return t`Phone call`;
    }
};

/**
 * Orchestrates ownership verification across the channels enabled for the
 * financing gate (email / SMS / voice).
 *
 * On mount it asks the server whether the user already satisfies the gate (or
 * is exempt) and, if so, passes immediately — a multi-channel picker can't
 * fold that into a send-on-mount the way a single-channel gate does, so this
 * pre-check is what prevents re-challenging an already-verified user. When
 * verification is still needed it always shows the method picker — even for a
 * single channel, so the SMS/voice consent disclosure is shown before any code
 * is sent — and delegates to the matching code-entry gate.
 */
export const VerificationGate = (props: IProps): React.JSX.Element => {
    const [phase, setPhase] = useState<Phase>("checking");
    // The channels we can actually offer the user. The set is the server's
    // offered channels (from the status pre-check) intersected with which
    // contact targets we actually hold — email needs props.email; sms/voice
    // need props.phone. An offered channel whose target is missing is dropped;
    // if that leaves nothing, the gate renders the recoverable "check your
    // contact info" state rather than a dead gate. Derived from the status
    // response so there's a single source of truth for what's on offer.
    const [choices, setChoices] = useState<readonly VerificationChannel[]>([]);
    const isCanceled = useRef(false);
    const didCheck = useRef(false);

    // The channels the financing gate is configured to accept (server-driven,
    // via the context processor). Used as the fail-safe offer set when the
    // status pre-check can't be reached — see the comment in the effect below.
    const configuredChannels = usePageSetting(
        "financing_verification_channels",
    );
    const failSafeChannels: readonly VerificationChannel[] =
        CHANNEL_ORDER.filter((channel) =>
            (configuredChannels ?? CHANNEL_ORDER).includes(channel),
        );
    // Privacy Policy / Terms of Use URLs for the on-screen TCPA consent copy.
    // Resolved per-site (footer settings / page slug); null when a site hasn't
    // configured them, in which case that link is simply omitted.
    const privacyURL = usePageURL("privacy_policy_link");
    const termsURL = usePageURL("terms_of_use_link");

    useEffect(() => {
        if (didCheck.current) {
            return;
        }
        didCheck.current = true;
        const check = async (): Promise<void> => {
            let satisfied = false;
            // Fail safe: if the status check fails we still require
            // verification, and fall back to the configured channels (NOT every
            // channel). Offering a channel the server won't accept would let a
            // user complete e.g. email OTP on an SMS/voice-only rollout and
            // then be rejected at submit, since the per-channel send endpoints
            // don't all re-check FINANCING_VERIFICATION_CHANNELS.
            let offered: readonly VerificationChannel[] = failSafeChannels;
            try {
                const status = await getVerificationStatus(
                    props.email,
                    props.phone,
                );
                if (status.status === "needs") {
                    offered = status.channels;
                } else {
                    satisfied = true;
                }
            } catch {
                // Keep the fail-safe (configured) channel set.
            }
            if (isCanceled.current) {
                return;
            }
            if (satisfied) {
                trackOtpEvent("otp_skipped", "verification_gate");
                props.onVerified();
                return;
            }
            // Intersect with target presence, preserving the email→sms→voice
            // display order (filter CHANNEL_ORDER, not the server's list).
            const offerable = CHANNEL_ORDER.filter(
                (channel) =>
                    offered.includes(channel) &&
                    (channel === "email" ? !!props.email : !!props.phone),
            );
            setChoices(offerable);
            if (offerable.length > 0) {
                trackOtpEvent("otp_prompt_shown", "verification_gate");
            }
            // Always show the channel-selection screen — never auto-skip into a
            // single channel. For SMS/voice it is where the TCPA consent
            // disclosure is shown before any code is sent, and it keeps one
            // consistent entry point across every configuration.
            setPhase("picker");
        };
        void check();
    }, []);

    const onSubGateCancel = (): void => {
        // From a sub-gate, "Edit my information" returns to the picker when
        // there's more than one method to choose from; otherwise it backs all
        // the way out to the form.
        if (choices.length > 1) {
            setPhase("picker");
            return;
        }
        isCanceled.current = true;
        props.onCancel();
    };

    const cancelToForm = (): void => {
        isCanceled.current = true;
        props.onCancel();
    };

    if (phase === "checking") {
        return (
            <div className={styles.wrapper}>
                <p role="status">{t`Checking your verification status…`}</p>
            </div>
        );
    }

    if (phase === "picker" && choices.length > 0) {
        const offersPhone = choices.some(
            (channel) => channel === "sms" || channel === "voice",
        );
        // Privacy/Terms links for the consent copy, omitted when the site
        // hasn't configured the URL.
        const termsLinkEl = termsURL ? (
            <a
                key="terms"
                href={isoWebPageURL.unwrap(termsURL)}
                target="_blank"
                rel="noopener noreferrer"
            >
                {t`Terms of Use`}
            </a>
        ) : null;
        const privacyLinkEl = privacyURL ? (
            <a
                key="privacy"
                href={isoWebPageURL.unwrap(privacyURL)}
                target="_blank"
                rel="noopener noreferrer"
            >
                {t`Privacy Policy`}
            </a>
        ) : null;
        const consentMessage = t`By providing us with your mobile number, you agree to receive a one-time text message with a passcode. Message and data rates apply.`;
        // The single-link branches share one translatable string ("See our
        // ${0}."); ttag keys on placeholder position, so emitting it from two
        // separate jt`` calls would collide on the same msgid. Resolve the lone
        // link first and emit the string once.
        const soloLinkEl = termsLinkEl ?? privacyLinkEl;
        const consentLinks =
            termsLinkEl && privacyLinkEl
                ? jt`See our ${termsLinkEl} and ${privacyLinkEl}.`
                : soloLinkEl
                  ? jt`See our ${soloLinkEl}.`
                  : null;
        return (
            <div className={styles.wrapper}>
                <h3 className={styles.heading}>{t`Verify your identity`}</h3>
                <p className={styles.instructions}>
                    {t`Choose how you'd like to receive your one-time verification code.`}
                </p>
                <div className={styles.choices}>
                    {choices.map((channel) => (
                        <button
                            key={channel}
                            type="button"
                            className="button"
                            onClick={() => {
                                setPhase(channel);
                            }}
                        >
                            {channelLabel(channel)}
                        </button>
                    ))}
                </div>
                {offersPhone && (
                    <p className={styles.consent}>
                        {consentMessage} {consentLinks}
                    </p>
                )}
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={cancelToForm}
                >
                    {t`Edit my information`}
                </button>
            </div>
        );
    }

    if (phase === "email" && props.email) {
        return (
            <EmailVerificationGate
                email={props.email}
                onVerified={props.onVerified}
                onCancel={onSubGateCancel}
            />
        );
    }

    if ((phase === "sms" || phase === "voice") && props.phone) {
        return (
            <PhoneVerificationGate
                phone={props.phone}
                channel={phase}
                onVerified={props.onVerified}
                onCancel={onSubGateCancel}
            />
        );
    }

    // The chosen channel's target is missing (shouldn't happen — the form
    // collects it). Surface a recoverable error rather than a blank screen.
    return (
        <div className={styles.wrapper}>
            <p role="alert">
                {t`We couldn't start verification. Please go back and check your contact information.`}
            </p>
            <button
                type="button"
                className={styles.linkButton}
                onClick={cancelToForm}
            >
                {t`Edit my information`}
            </button>
        </div>
    );
};
